import React, { type ReactNode } from 'react';

import type { MessageDescriptor } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { ConfluenceIcon, JiraIcon } from '@atlaskit/logo';
import { Box } from '@atlaskit/primitives/compiled';
import {
	ZeroQueryDateRange,
	ZeroQueryProduct,
	ZeroQueryType,
} from '@atlassian/search-client/zero-queries/types';

import { GoogleDriveIcon } from './3p-icons/google-icon';
import { SharepointIcon } from './3p-icons/sharepoint-icon';
import { SlackIcon } from './3p-icons/slack-icon';
import { messages } from './messages';

const styles = cssMap({
	icon3PContainer: {
		display: 'inline-flex',
	},
});

type Icons1P = Extract<ZeroQueryProduct, 'CONFLUENCE' | 'JIRA'>;
const get1PIcon = (product: Icons1P) => (): ReactNode => {
	let Icon = null;
	switch (product) {
		case ZeroQueryProduct.CONFLUENCE:
			Icon = ConfluenceIcon;
			break;
		case ZeroQueryProduct.JIRA:
			Icon = JiraIcon;
			break;
		default:
			return null;
	}

	return <Icon key={product} label="" size="xsmall" shouldUseNewLogoDesign={true} />;
};

type Icons3P = Extract<ZeroQueryProduct, 'GOOGLE' | 'SHAREPOINT' | 'SLACK'>;
const get3PIcon = (product: Icons3P) => (): ReactNode => {
	let Icon = null;
	switch (product) {
		case ZeroQueryProduct.GOOGLE:
			Icon = GoogleDriveIcon;
			break;
		case ZeroQueryProduct.SHAREPOINT:
			Icon = SharepointIcon;
			break;
		case ZeroQueryProduct.SLACK:
			Icon = SlackIcon;
			break;
		default:
			return null;
	}

	return (
		<Box
			aria-hidden
			key={`${product.toLowerCase()}-icon`}
			testId={`${product.toLowerCase()}-icon`}
			xcss={styles.icon3PContainer}
		>
			<Icon />
		</Box>
	);
};

const app1PIcons = {
	[ZeroQueryProduct.CONFLUENCE]: get1PIcon(ZeroQueryProduct.CONFLUENCE),
	[ZeroQueryProduct.JIRA]: get1PIcon(ZeroQueryProduct.JIRA),
};
// Note on non-null assertion operators: not all 3P configs have an `iconBorderless` property, but these are guaranteed
const app3PIcons = {
	[ZeroQueryProduct.GOOGLE]: get3PIcon(ZeroQueryProduct.GOOGLE),
	[ZeroQueryProduct.SLACK]: get3PIcon(ZeroQueryProduct.SLACK),
	[ZeroQueryProduct.SHAREPOINT]: get3PIcon(ZeroQueryProduct.SHAREPOINT),
};

export const appIcons: {
	GOOGLE: () => ReactNode;
	SLACK: () => ReactNode;
	SHAREPOINT: () => ReactNode;
	CONFLUENCE: () => ReactNode;
	JIRA: () => ReactNode;
} = {
	...app1PIcons,
	...app3PIcons,
};

// TODO: Remove this once BE sends purely structured data instead of a full result string
export const zeroQueryTemplates: Array<{
	type: ZeroQueryType;
	pattern: RegExp;
	message: MessageDescriptor;
}> = [
	{
		type: ZeroQueryType.COLLABORATOR_PROFILE,
		pattern: /:person: ([A-Za-z ]+)'s profile$/,
		message: messages.zero_query_collaborator_profile_suggestion,
	},
	{
		type: ZeroQueryType.CONFLUENCE_PAGES_CREATED,
		pattern: /:product: Confluence pages I created$/,
		message: messages.zero_query_confluence_pages_created_suggestion,
	},
	{
		type: ZeroQueryType.CONFLUENCE_PAGES_CREATED_30_DAYS,
		pattern: /:product: Confluence pages I created in the :date: last 30 days$/,
		message: messages.zero_query_confluence_pages_created_30_days_suggestion,
	},
	{
		type: ZeroQueryType.CONFLUENCE_PAGES_CREATED_7_DAYS,
		pattern: /:product: Confluence pages I created in the :date: last 7 days$/,
		message: messages.zero_query_confluence_pages_created_7_days_suggestion,
	},
	{
		type: ZeroQueryType.CONFLUENCE_PAGES_CREATED_COLLABORATOR,
		pattern: /:product: Confluence pages created by :person: ([A-Za-z ]+)$/,
		message: messages.zero_query_confluence_pages_created_collaborator_suggestion,
	},
	{
		type: ZeroQueryType.CONFLUENCE_PAGES_CREATED_COLLABORATOR_30_DAYS,
		pattern:
			/:product: Confluence pages created by :person: ([A-Za-z ]+) in the :date: last 30 days$/,
		message: messages.zero_query_confluence_pages_created_collaborator_last_30_days_suggestion,
	},
	{
		type: ZeroQueryType.CONFLUENCE_PAGES_CREATED_COLLABORATOR_7_DAYS,
		pattern:
			/:product: Confluence pages created by :person: ([A-Za-z ]+) in the :date: last 7 days$/,
		message: messages.zero_query_confluence_pages_created_collaborator_7_days_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_MY_WORK_ITEMS,
		pattern: /Show my :product: work items$/,
		message: messages.zero_query_jira_my_work_items_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Recent tickets created by me$/,
		message: messages.zero_query_jira_nlq_recent_tickets_created_by_me_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /What are my open tickets\?$/,
		message: messages.zero_query_jira_nlq_what_are_my_open_tickets_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Show tickets due this week$/,
		message: messages.zero_query_jira_nlq_show_tickets_due_this_week_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Issues I created in the last month$/,
		message: messages.zero_query_jira_nlq_issues_i_created_in_the_last_month_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Show high priority tickets assigned to me$/,
		message: messages.zero_query_jira_nlq_show_high_priority_tickets_assigned_to_me_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Show me all of my in-progress work items$/,
		message: messages.zero_query_jira_nlq_show_me_all_of_my_in_progress_work_items_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Issues assigned to me in the past 7 days$/,
		message: messages.zero_query_jira_nlq_issues_assigned_to_me_in_the_past_7_days_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Show all the bugs assigned to me$/,
		message: messages.zero_query_jira_nlq_show_all_the_bugs_assigned_to_me_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ,
		pattern: /Progress for epics created by me$/,
		message: messages.zero_query_jira_nlq_progress_for_epics_created_by_me_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_NLQ_COLLABORATOR,
		pattern: /Recent open items assigned to ([A-Za-z ]+)$/,
		message: messages.zero_query_jira_nlq_collaborator_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_TICKETS_ASSIGNED,
		pattern: /:product: Jira tickets assigned to me$/,
		message: messages.zero_query_jira_tickets_assigned_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_TICKETS_ASSIGNED_COLLABORATOR,
		pattern: /:product: tickets assigned to :person: ([A-Za-z ]+)$/,
		message: messages.zero_query_jira_tickets_assigned_collaborator_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_TICKETS_CREATED,
		pattern: /:product: Jira tickets I created$/,
		message: messages.zero_query_jira_tickets_created_suggestion,
	},
	{
		type: ZeroQueryType.JIRA_TICKETS_CREATED_COLLABORATOR,
		pattern: /:product: Jira tickets created by :person: ([A-Za-z ]+)$/,
		message: messages.zero_query_jira_tickets_created_collaborator_suggestion,
	},
	{
		type: ZeroQueryType.THIRD_PARTY_GOOGLE_COLLABORATOR_DOCS,
		pattern: /:product: Google docs from :person: ([A-Za-z ]+)$/,
		message: messages.zero_query_third_party_google_collaborator_docs_suggestion,
	},
	{
		type: ZeroQueryType.THIRD_PARTY_GOOGLE_MY_DOCS,
		pattern: /My :product: Google Drive docs$/,
		message: messages.zero_query_third_party_google_my_docs_suggestion,
	},
	{
		type: ZeroQueryType.THIRD_PARTY_SHAREPOINT_COLLABORATOR_DOCS,
		pattern: /:product: SharePoint docs from :person: ([A-Za-z ]+)$/,
		message: messages.zero_query_third_party_sharepoint_collaborator_docs_suggestion,
	},
	{
		type: ZeroQueryType.THIRD_PARTY_SHAREPOINT_MY_DOCS,
		pattern: /My :product: SharePoint documents$/,
		message: messages.zero_query_third_party_sharepoint_my_docs_suggestion,
	},
	{
		type: ZeroQueryType.THIRD_PARTY_SLACK_COLLABORATOR_MESSAGES,
		pattern: /:product: Slack messages from :person: ([A-Za-z ]+)$/,
		message: messages.zero_query_third_party_slack_collaborator_messages_suggestion,
	},
	{
		type: ZeroQueryType.THIRD_PARTY_SLACK_MY_MESSAGES,
		pattern: /My :product: Slack messages$/,
		message: messages.zero_query_third_party_slack_my_messages_suggestion,
	},
	{
		type: ZeroQueryType.WHO_IS,
		pattern: /Who is ([A-Za-z ]+)\?$/,
		message: messages.zero_query_who_is_suggestion,
	},
];

export const removeEntityPlaceholders: (text: string) => string = (text: string): string =>
	text
		// Replace :placeholder: with single space, handling surrounding whitespace
		.replace(/\s*:\w+:\s*/g, ' ')
		// Normalize multiple consecutive spaces
		.replace(/\s+/g, ' ')
		// Trim leading and trailing whitespace
		.replace(/^\s+|\s+$/g, '')
		// Remove spaces before punctuation
		.replace(/\s+([.,!?])/g, '$1');

const productParamMap = {
	[ZeroQueryProduct.CONFLUENCE]: 'confluence',
	[ZeroQueryProduct.JIRA]: 'jira',
	[ZeroQueryProduct.GOOGLE]: 'google',
	[ZeroQueryProduct.SLACK]: 'slack',
	[ZeroQueryProduct.SHAREPOINT]: 'sharepoint',
};

const dateRangeParamMap = {
	[ZeroQueryDateRange.TODAY]: 'today',
	[ZeroQueryDateRange.YESTERDAY]: 'yesterday',
	[ZeroQueryDateRange.PAST_7_DAYS]: 'past7Days',
	[ZeroQueryDateRange.PAST_30_DAYS]: 'past30Days',
};

export const getZeroQueryParams: (
	product?: ZeroQueryProduct,
	accountId?: string | null,
	dateRange?: ZeroQueryDateRange,
) => {
	product: string | undefined;
	contributors: string | undefined;
	lastModified: string | undefined;
} = (product?: ZeroQueryProduct, accountId?: string | null, dateRange?: ZeroQueryDateRange) => ({
	product: product ? productParamMap[product] : undefined,
	contributors: accountId ? accountId : undefined,
	lastModified: dateRange ? dateRangeParamMap[dateRange] : undefined,
});
